import React from 'react';
import Header from '../components/Header';
import Breadcrumb from '../components/Breadcrumb';
import PediatricCalculator from '../components/PediatricCalculator';
import ReferencesAndAuthor from '../components/ReferencesAndAuthor';
import Footer from '../components/Footer';

function Pediatrica() {
  return (
    <div className="pediatric-shell">
      <Header />
      <Breadcrumb />
      <main className="page editorial-grid pediatric-page">
        <span className="section-kicker">Pediatria · consulta rápida</span>
        <h1>Escala de Coma de Glasgow pediátrica: tabela por faixa etária</h1>
        <p className="pediatric-lead">Adaptação dos descritores para lactentes e crianças pré-verbais, mantendo as mesmas amplitudes de pontuação da escala adulta.</p>

        <div className="ped-summary">
          <strong>Resumo</strong>
          <ul>
            <li>• O 1–4 · V 1–5 · M 1–6 · total 3–15.</li>
            <li>• O que muda são os descritores, não os limites de pontuação.</li>
            <li>• Crianças pré-verbais exigem adaptação principalmente da resposta verbal.</li>
            <li>• Se um componente não puder ser avaliado de forma confiável, registre NT e não some o total.</li>
          </ul>
        </div>

        <section className="ped-prose-section">
          <h2>O que muda na Escala de Glasgow pediátrica?</h2>
          <p>A adaptação pediátrica preserva a estrutura O + V + M e a faixa total de 3 a 15. Em crianças pequenas, os descritores são ajustados ao desenvolvimento: interação social, choro, vocalização e comportamento motor esperado para a idade substituem tarefas que dependem de linguagem e orientação verbal maduras.</p>
        </section>

        <section className="ped-table-section wide">
          <h2>Tabela da Escala de Glasgow pediátrica</h2>
          <p>Descritores para crianças pré-verbais. Amplitudes idênticas à versão adulta: O 1–4 · V 1–5 · M 1–6 · total 3–15.</p>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Pontos</th>
                  <th>Abertura ocular (O)</th>
                  <th>Resposta verbal (V)</th>
                  <th>Resposta motora (M)</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>6</td><td>—</td><td>—</td><td>Movimenta-se espontânea e adequadamente para a idade (ou obedece)</td></tr>
                <tr><td>5</td><td>—</td><td>Balbucia, arrulha, sorriso social, fixa e segue objetos</td><td>Localiza: retira ao toque</td></tr>
                <tr><td>4</td><td>Espontânea</td><td>Choro irritado, mas consolável</td><td>Retirada à pressão (flexão normal)</td></tr>
                <tr><td>3</td><td>Ao som / à fala</td><td>Choro persistente, inconsolável; gemido à pressão</td><td>Flexão anormal (decorticação)</td></tr>
                <tr><td>2</td><td>À pressão</td><td>Agitação, inquietação inconsolável</td><td>Extensão (descerebração)</td></tr>
                <tr><td>1</td><td>Ausente</td><td>Ausente</td><td>Ausente</td></tr>
                <tr><td>NT</td><td>Edema periorbital, trauma ocular</td><td>Intubação, traqueostomia</td><td>Bloqueio neuromuscular, sedação profunda</td></tr>
              </tbody>
            </table>
          </div>
          <div className="ped-table-notes">
            <p><strong>A partir de ~2 anos:</strong> a resposta motora do adulto já pode ser aplicável conforme desenvolvimento.</p>
            <p><strong>A partir de ~5 anos:</strong> a escala do adulto pode ser aplicada integralmente, com perguntas de orientação adaptadas à idade.</p>
          </div>
        </section>

        <div className="wide ped-calculator-slot">
          <PediatricCalculator />
        </div>

        <div className="ped-question-grid wide">
          <section>
            <h2>Como avaliar a resposta verbal em lactentes?</h2>
            <p>Observe a qualidade da interação: sorriso social, fixação e seguimento, balbucio ou arrulho, além do padrão de choro. Diferencie choro consolável de choro persistente e inconsolável. O cuidador ajuda a reconhecer o comportamento habitual e a separar estranhamento, medo e alteração real do nível de consciência.</p>
          </section>

          <section>
            <h2>Como avaliar a resposta motora em lactentes?</h2>
            <p>Considere movimento espontâneo apropriado para a idade e a resposta ao toque ou à pressão. Reflexos primitivos e limitações motoras do desenvolvimento podem confundir a observação; registre fatores de interferência em vez de forçar uma pontuação que não represente a resposta examinada.</p>
          </section>

          <section>
            <h2>A partir de que idade se usa a escala do adulto?</h2>
            <p>A transição depende do desenvolvimento. Em torno de 2 anos, o componente motor adulto costuma se tornar aplicável; por volta de 5 anos, a escala adulta pode ser utilizada integralmente, ajustando as perguntas de orientação à capacidade esperada para a idade.</p>
          </section>

          <section>
            <h2>O que a Escala de Glasgow pediátrica não faz?</h2>
            <p>Ela não substitui avaliação neurológica pediátrica completa nem elimina a necessidade de interpretar medo, sono fisiológico, pós-ictal, hipoglicemia, febre e outras causas de alteração comportamental. Registre sempre os componentes e o contexto clínico observado.</p>
          </section>
        </div>

        <section className="ped-pitfalls">
          <h2>Armadilhas na avaliação pediátrica</h2>
          <ul>
            <li>• Avaliar em ambiente ruidoso ou sem permitir aproximação do cuidador.</li>
            <li>• Confundir medo, estranhamento ou sono fisiológico com rebaixamento.</li>
            <li>• Ignorar hipoglicemia, estado pós-ictal, febre e outras causas reversíveis.</li>
            <li>• Forçar um total quando algum componente é não testável.</li>
          </ul>
        </section>

        <section className="ped-faq">
          <h2>Perguntas frequentes</h2>
          <div className="ped-faq-list">
            <div><h3>Qual o Glasgow normal de um bebê?</h3><p>Uma criança sem alteração do nível de consciência deve alcançar a melhor resposta esperada para sua etapa de desenvolvimento em O, V e M; a adaptação pediátrica existe justamente para não penalizar quem ainda não fala ou não executa tarefas de adulto.</p></div>
            <div><h3>Como calcular Glasgow em criança que não fala?</h3><p>Use os descritores pediátricos da resposta verbal, baseados em interação, vocalização e choro, mantendo V de 1 a 5.</p></div>
            <div><h3>Glasgow pediátrico vai de 3 a 15?</h3><p>Sim. O 1–4, V 1–5 e M 1–6 mantêm as mesmas amplitudes; mudam os descritores adequados ao desenvolvimento.</p></div>
            <div><h3>Qual a diferença entre Glasgow pediátrico e adulto?</h3><p>A estrutura é a mesma. A diferença principal está nos descritores usados para julgar respostas em crianças que ainda não têm linguagem e comportamento motor comparáveis aos de um adulto.</p></div>
          </div>
        </section>

        <nav className="ped-related-nav" aria-label="Conteúdo relacionado">
          <a href="/#calculadora">→ Calculadora adulta</a>
          <a href="/tabela">→ Tabela do adulto</a>
          <a href="/como-avaliar">→ Como avaliar cada componente</a>
        </nav>

        <ReferencesAndAuthor />
      </main>
      <Footer />
    </div>
  );
}

export default Pediatrica;
